/**
 * Informe de Ingresos x Mayor (permiso de ares9750, como el informe de cobros).
 *
 * Consolidado de lo que entró por Ventas x Mayor en un rango de fechas:
 * ventas al contado (por factura) + cobros a facturas a crédito (por recibo,
 * incluye el inicial cobrado al facturar). Son las mismas fuentes que las
 * líneas "Ventas efectivo mayor" y "Pagos x mayor crédito" del cuadre.
 * Importes en la moneda base del negocio. Vistas: detalle, por cliente, por
 * forma de pago y por día; Excel con las cuatro hojas.
 * Backend: /api/xmayor_ingresos_reporte (ventasxmayorbackend.py).
 */
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, StatusPill, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi, useSucursales } from "lib/api";
import { hayOtraMoneda, monedaLabel } from "lib/moneda";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};
const r2 = (v) => Math.round(v * 100) / 100;

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin ingresos en el rango seleccionado
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

ResumenChip.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  color: PropTypes.string,
};
ResumenChip.defaultProps = { color: FP.color.navy };

// Agrupa los movimientos por una clave, separando contado y cobros.
function agrupar(movimientos, clave, etiqueta) {
  const mapa = new Map();
  movimientos.forEach((m) => {
    const k = clave(m);
    const g = mapa.get(k) || {
      id: k,
      nombre: etiqueta(m),
      contado: 0,
      cobros: 0,
      total: 0,
      documentos: 0,
      orden: m.fechaorden,
    };
    if (m.tipo === "Contado") g.contado += num(m.monto);
    else g.cobros += num(m.monto);
    g.total += num(m.monto);
    g.documentos += 1;
    mapa.set(k, g);
  });
  return Array.from(mapa.values()).map((g) => ({
    ...g,
    contado: r2(g.contado),
    cobros: r2(g.cobros),
    total: r2(g.total),
  }));
}

const VISTAS = [
  { valor: "detalle", label: "Detalle" },
  { valor: "cliente", label: "Por cliente" },
  { valor: "forma", label: "Por forma de pago" },
  { valor: "dia", label: "Por día" },
];

export default function IngresosXmayor() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const sucursales = useSucursales({ conTodos: true });

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [sucursal, setSucursal] = useState("0-TODOS");
  const [vista, setVista] = useState("detalle");

  const [movimientos, setMovimientos] = useState([]);
  const [totales, setTotales] = useState({ contado: 0, cobros: 0, total: 0 });
  const [base, setBase] = useState("DOP");
  const [loading, setLoading] = useState(false);
  const [procesado, setProcesado] = useState(null); // filtros del último proceso

  useEffect(() => {
    post("/api/xmayor_clientes", {}).then(({ ok, data }) => {
      if (ok) setClientes(data.clientes || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    if (!fecha1 || !fecha2) {
      showAlert({ message: "Indique el rango de fechas", color: "warning" });
      return;
    }
    if (fecha1 > fecha2) {
      showAlert({ message: "La fecha desde es mayor que la fecha hasta", color: "warning" });
      return;
    }
    setLoading(true);
    try {
      const { ok, data } = await post("/api/xmayor_ingresos_reporte", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        cliente: cliente?.id || "",
        sucursal,
      });
      if (ok) {
        setMovimientos(data.movimientos || []);
        setTotales({
          contado: num(data.totalcontado),
          cobros: num(data.totalcobros),
          total: num(data.total),
        });
        setBase(data.base || "DOP");
        setProcesado({ fecha1, fecha2, cliente, sucursal });
      } else {
        showAlert({ message: data.Error || "No se pudo generar el informe", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const porCliente = useMemo(
    () =>
      agrupar(
        movimientos,
        (m) => m.cliente_id || m.cliente,
        (m) => m.cliente || "(sin cliente)"
      ).sort((a, b) => b.total - a.total),
    [movimientos]
  );
  const porForma = useMemo(
    () =>
      agrupar(
        movimientos,
        (m) => m.formapago,
        (m) => m.formapago
      ).sort((a, b) => b.total - a.total),
    [movimientos]
  );
  const porDia = useMemo(
    () =>
      agrupar(
        movimientos,
        (m) => m.fecha,
        (m) => m.fecha
      ).sort((a, b) => String(a.orden).localeCompare(String(b.orden))),
    [movimientos]
  );

  const mezcla = hayOtraMoneda(movimientos, base);
  const mon = monedaLabel(base);
  const hayDatos = movimientos.length > 0;

  const subtitulo = procesado
    ? `Del ${fechaDMY(procesado.fecha1)} al ${fechaDMY(procesado.fecha2)}${
        procesado.cliente ? ` · ${procesado.cliente.descrip}` : ""
      }${
        procesado.sucursal && !procesado.sucursal.startsWith("0")
          ? ` · Sucursal ${procesado.sucursal}`
          : ""
      } · Valores en ${mon}`
    : "";

  // ---- Columnas en pantalla ----
  const colMonto = (field, headerName, width = 125) => ({
    field,
    headerName,
    width,
    align: "right",
    headerAlign: "right",
    valueFormatter: (value) => fmtMoney(value),
  });

  const columnasDetalle = [
    { field: "fecha", headerName: "Fecha", width: 105 },
    {
      field: "tipo",
      headerName: "Tipo",
      width: 130,
      renderCell: ({ row }) => (
        <StatusPill
          label={row.tipo === "Contado" ? "Venta contado" : "Cobro crédito"}
          variant={row.tipo === "Contado" ? "success" : "info"}
        />
      ),
    },
    {
      field: "documento",
      headerName: "Documento",
      width: 110,
      valueGetter: (_v, row) => `${row.tipo === "Contado" ? "Fact." : "Rec."} ${row.documento}`,
    },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    {
      field: "cantidad",
      headerName: "Chasis/cuotas",
      width: 110,
      align: "right",
      headerAlign: "right",
    },
    { field: "formapago", headerName: "Forma de pago", width: 130 },
    { field: "referencia", headerName: "Referencia", width: 120 },
    ...(mezcla
      ? [
          {
            field: "moneda",
            headerName: "Moneda",
            width: 85,
            valueFormatter: (value) => monedaLabel(value),
            description: `Moneda del documento. Los importes se muestran en ${mon}.`,
          },
        ]
      : []),
    colMonto("monto", "Monto"),
    { field: "sucursal", headerName: "Suc.", width: 65 },
    { field: "user", headerName: "Usuario", width: 110 },
  ];

  const columnasGrupo = (titulo) => [
    { field: "nombre", headerName: titulo, flex: 1, minWidth: 180 },
    {
      field: "documentos",
      headerName: "Documentos",
      width: 110,
      align: "right",
      headerAlign: "right",
    },
    colMonto("contado", "Ventas contado", 140),
    colMonto("cobros", "Cobros crédito", 140),
    colMonto("total", "Total", 140),
    {
      field: "porciento",
      headerName: "%",
      width: 80,
      align: "right",
      headerAlign: "right",
      valueGetter: (_v, row) => (totales.total ? (row.total * 100) / totales.total : 0),
      valueFormatter: (value) => `${num(value).toFixed(1)}%`,
    },
  ];

  const vistaActual = {
    detalle: { filas: movimientos, columnas: columnasDetalle },
    cliente: { filas: porCliente, columnas: columnasGrupo("Cliente") },
    forma: { filas: porForma, columnas: columnasGrupo("Forma de pago") },
    dia: { filas: porDia, columnas: columnasGrupo("Fecha") },
  }[vista];

  // ---- Excel e impresión ----
  const cabDetalle = [
    "Fecha",
    "Tipo",
    "Documento",
    "Cliente",
    "Chasis/cuotas",
    "Forma de pago",
    "Referencia",
    ...(mezcla ? ["Moneda"] : []),
    `Monto (${mon})`,
    "Suc.",
    "Usuario",
  ];
  const filaDetalle = (m, monto) => [
    m.fecha,
    m.tipo === "Contado" ? "Venta contado" : "Cobro crédito",
    `${m.tipo === "Contado" ? "Fact." : "Rec."} ${m.documento}`,
    m.cliente,
    m.cantidad,
    m.formapago,
    m.referencia,
    ...(mezcla ? [monedaLabel(m.moneda)] : []),
    monto,
    m.sucursal,
    m.user,
  ];
  const cabGrupo = (titulo) => [
    titulo,
    "Documentos",
    `Ventas contado (${mon})`,
    `Cobros crédito (${mon})`,
    `Total (${mon})`,
  ];
  const filaGrupo = (g, fmt) => [
    g.nombre,
    g.documentos,
    fmt(g.contado),
    fmt(g.cobros),
    fmt(g.total),
  ];
  const totalGrupo = (fmt) => [
    "TOTAL",
    movimientos.length,
    fmt(totales.contado),
    fmt(totales.cobros),
    fmt(totales.total),
  ];
  const totalDetalle = (valor) => {
    const f = cabDetalle.map(() => "");
    const i = cabDetalle.indexOf(`Monto (${mon})`);
    f[i - 1] = "TOTAL";
    f[i] = valor;
    return f;
  };

  const exportar = () => {
    const libro = XLSX.utils.book_new();
    const encabezado = [["Informe de Ingresos x Mayor"], [subtitulo], []];
    const hoja = (filas) => XLSX.utils.aoa_to_sheet([...encabezado, ...filas]);
    const resumen = hoja([
      ["Ventas al contado", totales.contado],
      ["Cobros a crédito", totales.cobros],
      ["Total ingresos", totales.total],
      ["Documentos", movimientos.length],
    ]);
    resumen["!cols"] = [{ wch: 24 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(libro, resumen, "Resumen");
    const detalle = hoja([
      cabDetalle,
      ...movimientos.map((m) => filaDetalle(m, num(m.monto))),
      [],
      totalDetalle(totales.total),
    ]);
    detalle["!cols"] = cabDetalle.map((c) => ({ wch: c === "Cliente" ? 34 : 15 }));
    XLSX.utils.book_append_sheet(libro, detalle, "Detalle");
    [
      ["Por cliente", "Cliente", porCliente],
      ["Por forma de pago", "Forma de pago", porForma],
      ["Por día", "Fecha", porDia],
    ].forEach(([nombre, titulo, filas]) => {
      const h = hoja([
        cabGrupo(titulo),
        ...filas.map((g) => filaGrupo(g, (v) => v)),
        [],
        totalGrupo((v) => v),
      ]);
      h["!cols"] = [{ wch: 34 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(libro, h, nombre);
    });
    XLSX.writeFile(libro, `IngresosXmayor_${hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    const titulo = `Informe de Ingresos x Mayor — ${VISTAS.find((v) => v.valor === vista).label}`;
    if (vista === "detalle") {
      imprimirListadoGrande({
        titulo,
        subtitulo,
        cabeceras: cabDetalle,
        filas: movimientos.map((m) => filaDetalle(m, fmtMoney(m.monto))),
        totales: totalDetalle(`${mon} ${fmtMoney(totales.total)}`),
        numericas: [cabDetalle.indexOf("Chasis/cuotas"), cabDetalle.indexOf(`Monto (${mon})`)],
      });
      return;
    }
    const titulos = { cliente: "Cliente", forma: "Forma de pago", dia: "Fecha" };
    imprimirListadoGrande({
      titulo,
      subtitulo,
      cabeceras: cabGrupo(titulos[vista]),
      filas: vistaActual.filas.map((g) => filaGrupo(g, fmtMoney)),
      totales: totalGrupo(fmtMoney),
      numericas: [1, 2, 3, 4],
    });
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Informe de Ingresos x Mayor"
        subtitle="Ventas al contado y cobros a crédito del módulo, consolidados por período"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={clientes}
                value={cliente}
                onChange={(_, v) => setCliente(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <TextField {...params} label="Cliente (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                sx={fpInputSx}
              >
                {sucursales.map((s) => (
                  <MenuItem key={s.label} value={s.label}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip
              label="VENTAS AL CONTADO"
              value={`${mon} ${fmtMoney(totales.contado)}`}
              color={FP.color.success}
            />
            <ResumenChip
              label="COBROS A CRÉDITO"
              value={`${mon} ${fmtMoney(totales.cobros)}`}
              color={FP.color.accentBlue}
            />
            <ResumenChip label="TOTAL INGRESOS" value={`${mon} ${fmtMoney(totales.total)}`} />
            <ResumenChip label="DOCUMENTOS" value={String(movimientos.length)} />
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mb: 1.6 }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={vista}
              onChange={(_, v) => v && setVista(v)}
              sx={{ flexWrap: "wrap" }}
            >
              {VISTAS.map((v) => (
                <ToggleButton key={v.valor} value={v.valor} sx={{ textTransform: "none", px: 2 }}>
                  {v.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={1.2}>
              <Button
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={exportar}
                disabled={!hayDatos}
                sx={btnOutline(FP.color.success)}
              >
                Excel
              </Button>
              <Button
                startIcon={<PrintOutlinedIcon />}
                onClick={imprimir}
                disabled={!hayDatos}
                sx={btnOutline(FP.color.navy)}
              >
                Imprimir
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              key={vista}
              autoHeight
              rows={vistaActual.filas}
              columns={vistaActual.columnas}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
          <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted, mt: 1.2 }}>
            Ventas al contado: facturas x mayor de contado (una fila por factura). Cobros a crédito:
            recibos aplicados a facturas a crédito, incluido el inicial cobrado al facturar. Las
            facturas y recibos anulados no suman.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
